import {customElement} from "lit/decorators/custom-element.js";
import {AbstractLitInputComponent} from "../../abstract-lit-input.component";
import {html} from "lit";
import {when} from "lit/directives/when.js";
import {UuidHelper} from "../../../helpers/uuid.helper";
import {property} from "lit/decorators/property.js";

/**
 * Whether the text typed into a number input differs from the current value, so that an unchanged
 * value does not fire `value-changed`. Exactly the semantics of the former loose `str != value`:
 * a missing value always differs, otherwise the text is compared as a number ('' counts as 0,
 * surrounding spaces are ignored, text that is no number always differs).
 */
export function isChangedNumberInput(input: string, value: number | null | undefined): boolean {
    return value === null || value === undefined || Number(input) !== value;
}

@customElement(`ha-input-number`)
export class HaInputNumber extends AbstractLitInputComponent<number> {

    @property({type: Boolean, attribute: `with-clear-button`})
    public withClearButton: boolean = false;

    @property({type: String})
    public postfix: string | null = null;

    private _id = UuidHelper.generateV7();

    protected render(): unknown {
        return html`
            ${when(!!this.label, () => html`
                <label for=${this._id}>${this.label}</label>
            `)}
            <div class="wrapper">
                <input type="number"
                    id=${this._id}
                    .value=${this.value}
                    @input=${this._onInput}
                    min="0"
                    ?disabled=${this.readonly}
                >
                ${when(!!this.postfix, () => html`
                    <span class="postfix">${this.postfix}</span>
                `)}
                ${when(this.withClearButton, () => html`
                    <i class="clear-button" @click=${this._onClickClearButton}></i>
                `)}
                ${this._renderErrors()}
            </div>
            ${this._renderHelperText()}
        `;
    }

    protected _onInput(evt: Event): void {
        if (this.readonly) {
            return;
        }

        const str: string = (evt.target as HTMLInputElement).value;
        if (isChangedNumberInput(str, this.value)) {
            this.value = parseInt(str);
            this.dispatchEvent(
                new CustomEvent<{ value: number }>(`value-changed`, {detail: {value: this.value!}})
            );
        }
    }

    protected _onClickClearButton(): void {
        this.value = 0;
    }

}